import { MutableRefObject, useEffect, useRef } from 'react';

import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import { PrimaryButtonIcon } from 'approot/shared/styles/buttons/primary-button.style';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import * as Styled from './activity-present-nav-controls.style';

type BaseProps = {
  isEnd: boolean;
  isStart: boolean;
  position?: 'left' | 'right';
  ariaLabel: string;
  ariaNavigationLabel: string;
  handleNext: () => void;
  handlePrevious: () => void;
};

export const ActivityBuilderNavControls = ({
  position,
  isEnd,
  isStart,
  ariaLabel,
  ariaNavigationLabel,
  paginationContainerRef,
  handleNext,
  handlePrevious,
}: BaseProps & {
  paginationContainerRef?: MutableRefObject<HTMLDivElement | null>;
}) => {
  return (
    <Styled.ActivityBuilderNavControls
      $position={position}
      aria-label={ariaNavigationLabel}
      tabIndex={-1}
      ref={paginationContainerRef}
    >
      <PrimaryButtonIcon
        type="button"
        $borderRadius={40}
        disabled={isEnd}
        style={{
          visibility: isEnd ? 'hidden' : 'visible',
        }}
        aria-label={`Go to next ${ariaLabel}`}
        onClick={handleNext}
        data-testid="next-button"
        data-selector="next-arrow"
      >
        <InlineSvg id="icon_arrow_left" $rotate={180} />
      </PrimaryButtonIcon>
      <SecondaryButtonIcon
        type="button"
        $borderRadius={40}
        disabled={isStart}
        style={{
          visibility: isStart ? 'hidden' : 'visible',
        }}
        aria-label={`Go to previous ${ariaLabel}`}
        onClick={handlePrevious}
        data-testid="prev-button"
        data-selector="prev-arrow"
      >
        <InlineSvg id="icon_arrow_left" />
      </SecondaryButtonIcon>
    </Styled.ActivityBuilderNavControls>
  );
};

export const ActivityBuilderDualNav = ({
  isEnd,
  isStart,
  ariaLabel,
  ariaNavigationLabel,
  handleNext,
  handlePrevious,
}: BaseProps) => {
  const paginationContainerRef = useRef<HTMLDivElement | null>(null);

  // focus can break out of current context because we disable buttons
  // when we hit the start or end. Here we refocus to fix that
  useEffect(() => {
    if (isEnd || isStart) {
      paginationContainerRef.current?.focus();
    }
  }, [isStart, isEnd]);

  return (
    <>
      <ActivityBuilderNavControls
        isEnd={isEnd}
        isStart={isStart}
        ariaLabel={ariaLabel}
        ariaNavigationLabel={ariaNavigationLabel}
        paginationContainerRef={paginationContainerRef}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
      />
      <ActivityBuilderNavControls
        isEnd={isEnd}
        isStart={isStart}
        ariaLabel={ariaLabel}
        ariaNavigationLabel={ariaNavigationLabel}
        position="right"
        handleNext={handleNext}
        handlePrevious={handlePrevious}
      />
    </>
  );
};

export const ActivityPresentNextControl = ({
  isEnd,
  ariaLabel,
  ariaNavigationLabel,
  handleNext,
}: {
  isEnd: boolean;
  ariaLabel: string;
  ariaNavigationLabel: string;
  handleNext: () => void;
}) => {
  const paginationContainerRef = useRef<HTMLDivElement | null>(null);

  // focus can break out of current context because we disable buttons
  // when we hit the start or end. Here we refocus to fix that
  useEffect(() => {
    if (isEnd) {
      paginationContainerRef.current?.focus();
    }
  }, [isEnd]);

  return (
    <Styled.ActivityBuilderNavControls
      $position="right"
      aria-label={ariaNavigationLabel}
      tabIndex={-1}
      ref={paginationContainerRef}
    >
      <PrimaryButtonIcon
        type="button"
        $borderRadius={40}
        disabled={isEnd}
        aria-label={`Go to next ${ariaLabel}`}
        onClick={handleNext}
        data-testid="next-button"
        data-selector="next-arrow"
      >
        <InlineSvg id="icon_arrow_left" $rotate={180} />
      </PrimaryButtonIcon>
    </Styled.ActivityBuilderNavControls>
  );
};

export const ActivityPresentPrevControl = ({
  isStart,
  ariaLabel,
  ariaNavigationLabel,
  handlePrev,
}: {
  isStart: boolean;
  ariaLabel: string;
  ariaNavigationLabel: string;
  handlePrev: () => void;
}) => {
  const paginationContainerRef = useRef<HTMLDivElement | null>(null);

  // focus can break out of current context because we disable buttons
  // when we hit the start or end. Here we refocus to fix that
  useEffect(() => {
    if (isStart) {
      paginationContainerRef.current?.focus();
    }
  }, [isStart]);

  return (
    <Styled.ActivityBuilderNavControls
      $position="left"
      aria-label={ariaNavigationLabel}
      tabIndex={-1}
      ref={paginationContainerRef}
    >
      <SecondaryButtonIcon
        type="button"
        $borderRadius={40}
        disabled={isStart}
        aria-label={`Go to previous ${ariaLabel}`}
        onClick={handlePrev}
        data-testid="prev-button"
        data-selector="prev-arrow"
      >
        <InlineSvg id="icon_arrow_left" />
      </SecondaryButtonIcon>
    </Styled.ActivityBuilderNavControls>
  );
};
